import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { managementApi } from '@/shared/api/services'

export interface KnowledgeItem {
  id: string
  title: string
  category: string
  tags: string[]
  content: string
  createdAt: string
  updatedAt: string
  views: number
  // 后端实际字段
  file_name: string
  file_type: string
  file_size: number
  document_description: string
  status: string
  total_chunks: number
  created_at: string
}

export interface KnowledgeFilter {
  searchText: string
  category: string
  tags: string[]
  dateRange?: [string, string]
}

export interface KnowledgeState {
  items: KnowledgeItem[]
  filteredItems: KnowledgeItem[]
  filter: KnowledgeFilter
  selectedItem: KnowledgeItem | null
  isLoading: boolean
  error: string | null
  isEditing: boolean

  // Actions
  setFilter: (filter: Partial<KnowledgeFilter>) => void
  setSelectedItem: (item: KnowledgeItem | null) => void
  fetchItems: () => Promise<void>
  addItem: (item: Omit<KnowledgeItem, 'id' | 'createdAt' | 'updatedAt' | 'views'>) => Promise<void>
  updateItem: (id: string, updates: Partial<KnowledgeItem>) => Promise<void>
  deleteItem: (id: string) => Promise<void>
  searchItems: (query: string) => void
  startEditing: () => void
  cancelEditing: () => void
}

const initialState = {
  items: [],
  filteredItems: [],
  filter: {
    searchText: '',
    category: '',
    tags: [],
  },
  selectedItem: null,
  isLoading: false,
  error: null,
  isEditing: false,
}

export const useKnowledgeStore = create<KnowledgeState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setFilter: (filter) => {
        set((state) => ({
          filter: { ...state.filter, ...filter },
        }))
        get().searchItems(get().filter.searchText)
      },

      setSelectedItem: (item) => {
        set({ selectedItem: item })
      },

      fetchItems: async () => {
        set({ isLoading: true, error: null })
        const maxRetries = 2
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const response = await managementApi.getDocuments()
            const documents = response?.documents || []
            const knowledgeItems: KnowledgeItem[] = documents.map((doc: any) => ({
              id: doc.id || '',
              title: doc.document_description || doc.file_name || '未命名文档',
              category: doc.file_type ? `文档 (${doc.file_type})` : '文档',
              tags: doc.file_type ? [doc.file_type] : [],
              content: doc.document_description || `文件: ${doc.file_name}, 大小: ${doc.file_size} 字节`,
              createdAt: doc.created_at || new Date().toISOString(),
              updatedAt: doc.created_at || new Date().toISOString(),
              views: doc.total_chunks || 0,
              file_name: doc.file_name || '',
              file_type: doc.file_type || '',
              file_size: doc.file_size || 0,
              document_description: doc.document_description || '',
              status: doc.status || 'unknown',
              total_chunks: doc.total_chunks || 0,
              created_at: doc.created_at || new Date().toISOString()
            }))
            set({ items: knowledgeItems, filteredItems: knowledgeItems, isLoading: false })
            return
          } catch (error) {
            if (attempt === maxRetries) {
              console.error('获取知识库数据失败:', error)
              set({ error: '获取知识库数据失败，请检查后端服务', isLoading: false })
            } else {
              await new Promise(r => setTimeout(r, 500))
            }
          }
        }
      },

      addItem: async (item) => {
        set({ isLoading: true, error: null })
        try {
          // 注意：后端暂不支持手动添加文本知识条目
          // 请使用文件上传功能添加文档到知识库
          throw new Error('后端暂不支持手动添加文本知识条目，请使用文件上传功能')
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : '添加知识失败'
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      updateItem: async (id, updates) => {
        set({ isLoading: true, error: null })
        try {
          // 注意：后端暂不支持更新知识库文档
          // 请删除后重新上传更新后的文档
          throw new Error('后端暂不支持更新知识库文档，请删除后重新上传更新后的文档')
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : '更新知识失败'
          set({ error: errorMsg, isLoading: false })
          throw error
        }
      },

      deleteItem: async (id) => {
        set({ isLoading: true, error: null })
        try {
          // 调用真实API删除文档
          await managementApi.deleteDocument(id)

          // 从本地状态移除
          set((state) => ({
            items: state.items.filter((item) => item.id !== id),
            filteredItems: state.filteredItems.filter((item) => item.id !== id),
            isLoading: false,
          }))
        } catch (error) {
          console.error('删除文档失败:', error)
          set({ error: '删除文档失败', isLoading: false })
        }
      },

      searchItems: (query) => {
        const { items } = get()
        if (!query.trim()) {
          set({ filteredItems: items })
          return
        }

        const filtered = items.filter(
          (item) =>
            item.title.toLowerCase().includes(query.toLowerCase()) ||
            item.category.toLowerCase().includes(query.toLowerCase()) ||
            item.content.toLowerCase().includes(query.toLowerCase()) ||
            item.tags.some((tag) => tag.toLowerCase().includes(query.toLowerCase()))
        )
        set({ filteredItems: filtered })
      },

      startEditing: () => {
        set({ isEditing: true })
      },

      cancelEditing: () => {
        set({ isEditing: false, selectedItem: null })
      },
    }),
    { name: 'KnowledgeStore' }
  )
)